import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listExaminations, deleteExamination } from "@/lib/api";
import { Search, Trash2, FileText, ChevronRight, Loader2, GitCompareArrows, X, HardHat } from "lucide-react";
import { toast } from "sonner";

const STANDARD_FILTERS = [
  { value: "all", label: "Semua" },
  { value: "ilo", label: "ILO", Icon: HardHat },
  { value: "general", label: "Umum" },
];

export default function History() {
  const navigate = useNavigate();
  const [allItems, setAllItems] = useState([]);
  const [q, setQ] = useState("");
  const [standard, setStandard] = useState("all");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const items = useMemo(() => {
    if (standard === "all") return allItems;
    return allItems.filter((e) => (e.report_standard || "general") === standard);
  }, [allItems, standard]);
  const iloCount = useMemo(() => allItems.filter((e) => e.report_standard === "ilo").length, [allItems]);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const data = await listExaminations(query);
      setAllItems(data);
    } catch (e) {
      toast.error("Gagal memuat riwayat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus pemeriksaan ini?")) return;
    setDeleting(id);
    try {
      await deleteExamination(id);
      setAllItems((prev) => prev.filter((e) => e.id !== id));
      toast.success("Pemeriksaan dihapus");
    } catch (e) {
      toast.error("Gagal menghapus");
    } finally {
      setDeleting(null);
    }
  };

  const toggleCompareMode = () => {
    setCompareMode((v) => !v);
    setSelectedIds([]);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) {
        // Replace oldest
        return [prev[1], id];
      }
      return [...prev, id];
    });
  };

  const openComparison = () => {
    if (selectedIds.length !== 2) return;
    // First selected = "current" (main), second = "compare with"
    navigate(`/exam/${selectedIds[0]}?compare=${selectedIds[1]}`);
  };

  const total = items.length;
  const withFindings = useMemo(
    () =>
      items.filter(
        (x) => (x.predictions || []).some((p) => p.class !== "normal")
      ).length,
    [items]
  );

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1600px] mx-auto">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
          Records
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">
          Riwayat Pemeriksaan
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          {total} record{total !== 1 ? "s" : ""} · {withFindings} dengan temuan
          {iloCount > 0 && <span className="text-teal-400"> · {iloCount} standar ILO</span>}
        </p>
      </div>

      <div className="mb-5 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            data-testid="history-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama pasien, MR number, atau diagnosis..."
            className="block w-full pl-10 pr-4 py-3 rounded-lg border border-zinc-800 bg-zinc-900/40 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
          />
        </div>
        <div
          className="inline-flex rounded-lg border border-zinc-800 bg-zinc-900/40 p-1"
          data-testid="history-standard-filter"
        >
          {STANDARD_FILTERS.map(({ value, label, Icon }) => {
            const active = standard === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setStandard(value)}
                data-testid={`history-standard-filter-${value}`}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                  active
                    ? value === "ilo"
                      ? "bg-teal-600 text-white"
                      : "bg-zinc-800 text-zinc-100"
                    : "text-zinc-400 hover:text-zinc-100"
                }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {label}
                {value === "ilo" && iloCount > 0 && (
                  <span className={`font-mono text-[10px] px-1 rounded ${active ? "bg-teal-700" : "bg-zinc-800"}`}>
                    {iloCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          onClick={toggleCompareMode}
          data-testid="toggle-compare-mode-btn"
          className={`inline-flex items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
            compareMode
              ? "border-indigo-500/40 bg-indigo-500/10 text-indigo-300"
              : "border-zinc-800 bg-zinc-900/40 text-zinc-300 hover:border-zinc-700"
          }`}
        >
          <GitCompareArrows className="w-4 h-4" />
          {compareMode ? "Batal Bandingkan" : "Bandingkan 2 Exam"}
        </button>
      </div>

      {compareMode && (
        <div
          data-testid="compare-selection-bar"
          className="mb-5 rounded-xl border border-indigo-500/30 bg-indigo-500/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="text-sm text-zinc-200">
            <span className="text-indigo-300 font-semibold">
              {selectedIds.length}/2
            </span>{" "}
            exam dipilih.{" "}
            <span className="text-zinc-400">
              {selectedIds.length === 0
                ? "Klik 2 exam yang ingin dibandingkan."
                : selectedIds.length === 1
                ? "Pilih 1 lagi."
                : "Klik 'Buka Perbandingan' untuk lanjut."}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <button
                onClick={() => setSelectedIds([])}
                className="p-1.5 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={openComparison}
              disabled={selectedIds.length !== 2}
              data-testid="open-compare-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-sm font-semibold transition-colors"
            >
              <GitCompareArrows className="w-4 h-4" />
              Buka Perbandingan
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-zinc-900 bg-zinc-900/40 overflow-hidden">
        {loading && (
          <div className="p-10 flex items-center justify-center text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Memuat...
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-16 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-zinc-800 flex items-center justify-center mb-3">
              <FileText className="w-6 h-6 text-zinc-500" />
            </div>
            <div className="text-sm text-zinc-300 font-medium">
              {standard === "ilo" ? "Belum ada pemeriksaan standar ILO" : "Belum ada pemeriksaan"}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {standard === "ilo"
                ? "Pilih Standar Pelaporan ILO saat analisa baru, atau aktifkan di halaman detail exam."
                : "Analisa X-ray baru untuk mulai membuat record."}
            </div>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="divide-y divide-zinc-900">
            <div className="hidden md:grid md:grid-cols-[1fr_180px_120px_140px_120px_60px] gap-3 px-5 py-3 text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
              <div>Patient</div>
              <div>MR Number</div>
              <div>Findings</div>
              <div>Date</div>
              <div>Runtime</div>
              <div />
            </div>
            {items.map((exam) => {
              const findings = (exam.predictions || []).filter((p) => p.class !== "normal").length;
              const isSelected = selectedIds.includes(exam.id);
              const RowInner = (
                <>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-100 truncate">
                      {exam.patient?.name || "Unnamed Patient"}
                      {exam.report_standard === "ilo" && <IloBadge exam={exam} />}
                    </div>
                    {exam.final_diagnosis && (
                      <div className="text-xs text-zinc-500 truncate mt-0.5">
                        {exam.final_diagnosis}
                      </div>
                    )}
                  </div>
                  <div className="text-xs font-mono text-zinc-400">
                    {exam.patient?.patient_id || "-"}
                  </div>
                  <div>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                        findings > 0
                          ? "bg-rose-500/10 text-rose-400"
                          : "bg-emerald-500/10 text-emerald-400"
                      }`}
                    >
                      {findings > 0 ? `${findings} finding${findings > 1 ? "s" : ""}` : "Normal"}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-400">
                    {new Date(exam.created_at).toLocaleString()}
                  </div>
                  <div className="text-xs font-mono text-zinc-500">
                    {exam.inference_time_ms ? `${Math.round(exam.inference_time_ms)} ms` : "-"}
                  </div>
                </>
              );
              return compareMode ? (
                <button
                  key={exam.id}
                  onClick={() => toggleSelect(exam.id)}
                  data-testid={`history-row-compare-${exam.id}`}
                  className={`w-full grid grid-cols-1 md:grid-cols-[24px_1fr_180px_120px_140px_120px] gap-3 px-5 py-4 text-left transition-colors items-center ${
                    isSelected
                      ? "bg-indigo-500/10 hover:bg-indigo-500/15"
                      : "hover:bg-zinc-900/60"
                  }`}
                >
                  <div className="flex items-center justify-center">
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected
                          ? "border-indigo-400 bg-indigo-500 text-white"
                          : "border-zinc-700 bg-zinc-950"
                      }`}
                    >
                      {isSelected && (
                        <span className="text-[10px] font-bold">
                          {selectedIds.indexOf(exam.id) + 1}
                        </span>
                      )}
                    </div>
                  </div>
                  {RowInner}
                </button>
              ) : (
                <div
                  key={exam.id}
                  data-testid={`history-row-${exam.id}`}
                  className="grid grid-cols-1 md:grid-cols-[1fr_180px_120px_140px_120px_60px] gap-3 px-5 py-4 hover:bg-zinc-900/60 transition-colors group items-center"
                >
                  <Link to={`/exam/${exam.id}`} className="min-w-0">
                    <div className="text-sm font-medium text-zinc-100 truncate">
                      {exam.patient?.name || "Unnamed Patient"}
                      {exam.report_standard === "ilo" && <IloBadge exam={exam} />}
                    </div>
                    {exam.final_diagnosis && (
                      <div className="text-xs text-zinc-500 truncate mt-0.5">
                        {exam.final_diagnosis}
                      </div>
                    )}
                  </Link>
                  <div className="text-xs font-mono text-zinc-400">
                    {exam.patient?.patient_id || "-"}
                  </div>
                  <div>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
                        findings > 0
                          ? "bg-rose-500/10 text-rose-400"
                          : "bg-emerald-500/10 text-emerald-400"
                      }`}
                    >
                      {findings > 0 ? `${findings} finding${findings > 1 ? "s" : ""}` : "Normal"}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-400">
                    {new Date(exam.created_at).toLocaleString()}
                  </div>
                  <div className="text-xs font-mono text-zinc-500">
                    {exam.inference_time_ms ? `${Math.round(exam.inference_time_ms)} ms` : "-"}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <Link
                      to={`/exam/${exam.id}`}
                      data-testid={`open-exam-${exam.id}`}
                      className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => handleDelete(exam.id)}
                      disabled={deleting === exam.id}
                      data-testid={`delete-exam-${exam.id}`}
                      className="p-1.5 rounded hover:bg-rose-500/10 text-zinc-500 hover:text-rose-400 transition-colors disabled:opacity-50"
                    >
                      {deleting === exam.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function IloBadge({ exam }) {
  return (
    <span
      className="ml-1.5 align-middle text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-300 border border-teal-500/20"
      data-testid={`history-ilo-badge-${exam.id}`}
    >
      ILO{exam.ilo_summary ? ` · ${exam.ilo_summary}` : ""}
    </span>
  );
}
